import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/shared/Primitives";
import { Button } from "../components/ui/button";
import { YearNavigator } from "../components/shared/Navigators";
import EntryModal from "../components/shared/EntryModal";
import { Plus, CalendarPlus, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function Networking() {
  const [events, setEvents] = useState([]);
  const [evtModal, setEvtModal] = useState({ open: false, entry: null });
  const [contacts, setContacts] = useState({});
  const [contactModal, setContactModal] = useState({ open: false, eventId: null, entry: null });
  const [openEvent, setOpenEvent] = useState(null);

  const [groupByMonth, setGroupByMonth] = useState(
    () => (localStorage.getItem("rw_net_grp") ?? "on") !== "off"
  );
  const [year, setYear] = useState(String(new Date().getFullYear()));
  // Months open by default = current month only (of the current year); others collapsed.
  const currentMonthIdx = new Date().getMonth();
  const [openMonths, setOpenMonths] = useState(() => new Set([currentMonthIdx]));

  useEffect(() => {
    localStorage.setItem("rw_net_grp", groupByMonth ? "on" : "off");
  }, [groupByMonth]);

  const load = async () => {
    const { data } = await api.get("/networking/events");
    setEvents(data);
  };
  useEffect(() => { load(); }, []);

  const loadContacts = async (eid) => {
    const { data } = await api.get(`/networking/events/${eid}/contacts`);
    setContacts((c) => ({ ...c, [eid]: data }));
  };

  const saveEvent = async (p) => {
    if (p.id) await api.put(`/networking/events/${p.id}`, p);
    else await api.post("/networking/events", p);
    toast.success("Event saved");
    load();
  };

  const deleteEvent = async () => {
    if (evtModal.entry?.id) {
      await api.delete(`/networking/events/${evtModal.entry.id}`);
      toast.success("Event deleted");
      load();
    }
  };

  const saveLink = async (p) => {
    if (p.id) await api.put(`/networking/contacts/${p.id}`, p);
    else await api.post(`/networking/events/${contactModal.eventId}/contacts`, p);
    toast.success("Contact saved");
    loadContacts(contactModal.eventId);
    load();
  };

  const deleteLink = async () => {
    if (contactModal.entry?.id) {
      await api.delete(`/networking/contacts/${contactModal.entry.id}`);
      loadContacts(contactModal.eventId);
      load();
    }
  };

  // Bucket events by month for the selected year.
  const buckets = useMemo(() => {
    const byMonth = Array.from({ length: 12 }, () => []);
    const undated = [];
    const otherYear = [];
    for (const e of events) {
      if (!e.date) {
        undated.push(e);
        continue;
      }
      const [yr, mo] = e.date.split("-");
      if (yr === year) {
        const idx = parseInt(mo, 10) - 1;
        if (idx >= 0 && idx <= 11) byMonth[idx].push(e);
        else undated.push(e);
      } else {
        otherYear.push(e);
      }
    }
    byMonth.forEach((arr) => arr.sort((a, b) => (b.date || "").localeCompare(a.date || "")));
    return { byMonth, undated, otherYearCount: otherYear.length };
  }, [events, year]);

  const toggleMonth = (idx) =>
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const renderEventCard = (e) => (
    <div key={e.id} className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5 flex flex-col" data-testid={`event-card-${e.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{e.date || "—"}</div>
          <h3 className="font-display text-xl font-bold mt-1">{e.name}</h3>
        </div>
        <button onClick={() => setEvtModal({ open: true, entry: e })} className="text-xs text-neutral-400 hover:text-neutral-700" data-testid={`edit-event-${e.id}`}>
          Edit
        </button>
      </div>
      <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{e.notes || "No notes."}</p>
      <div className="flex items-center gap-4 mt-4 text-xs text-neutral-500">
        <div><strong className="text-neutral-800">{e.contact_count || 0}</strong> contacts</div>
        <div><strong className="text-neutral-800">{e.followup_count || 0}</strong> follow-ups</div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpenEvent(openEvent === e.id ? null : e.id);
            if (openEvent !== e.id) loadContacts(e.id);
          }}
          data-testid={`view-event-${e.id}`}
        >
          {openEvent === e.id ? "Hide" : "View"} Contacts
        </Button>
        <Button size="sm" className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]" onClick={() => setContactModal({ open: true, eventId: e.id, entry: null })} data-testid={`add-event-contact-${e.id}`}>
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
      {openEvent === e.id && (
        <div className="mt-4 border-t border-[var(--rw-border)] pt-3 space-y-2">
          {(contacts[e.id] || []).length === 0 && (
            <div className="text-xs text-neutral-400">No contacts yet.</div>
          )}
          {(contacts[e.id] || []).map((c) => (
            <button key={c.id} className="block w-full text-left px-3 py-2 bg-[var(--rw-surface-2)] rounded-[4px] hover:bg-neutral-200 text-sm" onClick={() => setContactModal({ open: true, eventId: e.id, entry: c })} data-testid={`event-contact-${c.id}`}>
              <div className="flex justify-between items-center">
                <span className="font-semibold">{c.name || c.individual || "—"}</span>
                <span className="text-xs text-neutral-500">{c.company}</span>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{c.notes || c.follow_up}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader
        eyebrow="Events"
        title="Networking"
        description="Networking events bucketed by month, each with its connected contacts."
        right={
          <>
            <Button
              variant="outline"
              onClick={() => setGroupByMonth((v) => !v)}
              aria-pressed={groupByMonth}
              data-testid="net-group-toggle"
              className={groupByMonth ? "bg-[var(--rw-surface-2)]" : ""}
            >
              {groupByMonth ? "By month: on" : "By month: off"}
            </Button>
            <Button
              className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
              onClick={() => setEvtModal({ open: true, entry: null })}
              data-testid="new-event-btn"
            >
              <CalendarPlus size={14} className="mr-2" /> New Event
            </Button>
          </>
        }
      />

      <div className="p-8 space-y-4">
        {groupByMonth ? (
          <>
            <div className="flex items-center gap-3">
              <YearNavigator value={year} onChange={setYear} />
              {buckets.otherYearCount > 0 && (
                <span className="text-xs text-neutral-500 italic">
                  {buckets.otherYearCount} event{buckets.otherYearCount === 1 ? "" : "s"} in other years — change year to view.
                </span>
              )}
            </div>

            {buckets.undated.length > 0 && (
              <div className="bg-white border border-dashed border-[var(--rw-border)] rounded-[4px]">
                <button
                  type="button"
                  onClick={() => toggleMonth(-1)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--rw-surface-2)] text-left"
                  data-testid="net-month-undated"
                >
                  {openMonths.has(-1) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="w-2 h-2 rounded-full bg-neutral-400" />
                  <span className="font-display text-base tracking-wide italic text-neutral-600">Undated</span>
                  <span className="text-[10px] uppercase tracking-[0.08em] text-neutral-500 ml-1">
                    {buckets.undated.length} event{buckets.undated.length === 1 ? "" : "s"}
                  </span>
                </button>
                {openMonths.has(-1) && (
                  <div className="border-t border-[var(--rw-border)] p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {buckets.undated.map(renderEventCard)}
                  </div>
                )}
              </div>
            )}

            {MONTHS.map((label, idx) => {
              const bucket = buckets.byMonth[idx];
              const isOpen = openMonths.has(idx);
              const isEmpty = bucket.length === 0;
              return (
                <div key={label} className={`bg-white border border-[var(--rw-border)] rounded-[4px] ${isEmpty ? "opacity-70" : ""}`} data-testid={`net-month-${label}`}>
                  <button
                    type="button"
                    onClick={() => toggleMonth(idx)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--rw-surface-2)] text-left"
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="w-2 h-2 rounded-full bg-[#8E4EC6]" />
                    <span className="font-display text-base tracking-wide">
                      {label} {year}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.08em] text-neutral-500 ml-1">
                      {bucket.length} event{bucket.length === 1 ? "" : "s"}
                    </span>
                    {idx === currentMonthIdx && String(new Date().getFullYear()) === year && (
                      <span className="text-[10px] uppercase tracking-[0.08em] text-[#E5484D] font-semibold ml-1">
                        · this month
                      </span>
                    )}
                  </button>
                  {isOpen && (
                    <div className="border-t border-[var(--rw-border)] p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {isEmpty ? (
                        <div className="col-span-full text-center text-sm text-neutral-400 py-6">
                          No events in {label} {year}.
                        </div>
                      ) : (
                        bucket.map(renderEventCard)
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.length === 0 && (
              <div className="col-span-full bg-white border border-[var(--rw-border)] rounded-[4px] p-10 text-center text-neutral-400">
                No events yet — create your first networking event.
              </div>
            )}
            {events.map(renderEventCard)}
          </div>
        )}
      </div>

      <EntryModal
        open={evtModal.open}
        onOpenChange={(v) => setEvtModal({ ...evtModal, open: v })}
        title={evtModal.entry ? "Edit Event" : "New Event"}
        entry={evtModal.entry}
        fields={[
          { name: "name", label: "Event Name", full: true },
          { name: "date", label: "Event Date", type: "date" },
          { name: "location", label: "Location" },
          { name: "notes", label: "Notes", type: "textarea", full: true },
        ]}
        promptAddContact={false}
        attachmentsTargetKind="event"
        onSave={saveEvent}
        onDelete={deleteEvent}
      />

      <EntryModal
        open={contactModal.open}
        onOpenChange={(v) => setContactModal({ ...contactModal, open: v })}
        title={contactModal.entry ? "Edit Event Contact" : "Add Event Contact"}
        entry={contactModal.entry}
        fields={[
          { name: "name", label: "Full Name" },
          { name: "company", label: "Company" },
          { name: "email", label: "Email" },
          { name: "phone", label: "Phone" },
          { name: "notes", label: "Notes", type: "textarea", full: true },
          { name: "follow_up", label: "Follow Up", full: true },
          { name: "follow_up_date", label: "Follow Up Date", type: "date" },
          { name: "status", label: "Status", type: "select", options: ["Not Started", "In Progress", "Completed", "Cancelled"] },
          { name: "priority", label: "Priority", type: "select", options: ["Normal", "Priority"] },
        ]}
        onSave={saveLink}
        onDelete={deleteLink}
      />
    </div>
  );
}
